import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkWidget,
  createWidget,
  deleteWidget,
  fetchWidgetThumbnail,
  getWidget,
  listWidgetVersions,
  listWidgets,
  restoreWidgetVersion,
  updateWidget,
  type WidgetListFilters,
} from '../api/widget-definitions.api';
import { widgetQueryKeys } from './query-keys';

export const useWidgetDefinitions = (filters: WidgetListFilters = {}, enabled = true) =>
  useQuery({
    queryKey: widgetQueryKeys.list(filters),
    queryFn: () => listWidgets(filters),
    enabled,
  });

export const useWidgetDefinition = (id: string | undefined) =>
  useQuery({
    queryKey: widgetQueryKeys.detail(id ?? ''),
    queryFn: () => getWidget(id as string),
    enabled: Boolean(id),
  });

export const useWidgetVersions = (id: string) =>
  useQuery({ queryKey: widgetQueryKeys.versions(id), queryFn: () => listWidgetVersions(id) });

/** The checks publishing runs, for the draft as it is at this revision. */
export const useWidgetCheck = (id: string, draftRevision: number) =>
  useQuery({ queryKey: widgetQueryKeys.check(id, draftRevision), queryFn: () => checkWidget(id) });

/** An object URL for the widget's latest preview, freed when no longer shown; null while it has none. */
export function useWidgetThumbnail(id: string): string | null {
  const { data } = useQuery({
    queryKey: widgetQueryKeys.thumbnail(id),
    queryFn: () => fetchWidgetThumbnail(id),
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!data) {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(data);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [data]);
  return url;
}

export function useCreateWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      idempotencyKey,
      ...body
    }: Parameters<typeof createWidget>[0] & { idempotencyKey: string }) =>
      createWidget(body, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: widgetQueryKeys.all }),
  });
}

export function useUpdateWidget(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof updateWidget>[1]) => updateWidget(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: widgetQueryKeys.all }),
  });
}

export function useRestoreWidgetVersion(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { version: number; draftRevision: number }) =>
      restoreWidgetVersion(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: widgetQueryKeys.all }),
  });
}

export function useDeleteWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWidget(id),
    onSuccess: (_, id) => {
      // The deleted widget's own queries are left to expire: still open on the page until this
      // settles, a refetch or a removal would read it again and fail as not found.
      return queryClient.invalidateQueries({
        queryKey: widgetQueryKeys.all,
        predicate: (query) => !query.queryKey.some((part) => part === id),
      });
    },
  });
}
